import { useParams, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import BaseLayout from "@/v2/components/BaseLayout";
import Transparency from "@/v2/components/Transparency";
import { fetchTrendBySlug } from "@/v2/queries";
import { decodeEntities, stripHtml } from "@/v2/textUtils";
import { amneLabel, FAKTAKARAKTAR_LABEL } from "@/v2/taxonomy";
import NotFound from "@/pages/NotFound";

const SITE = "https://korthet.se";

function hostOf(url: string): string {
  try {
    return new URL(url).hostname.replace(/^www\./, "");
  } catch {
    return url;
  }
}

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString("sv-SE", {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  } catch {
    return "";
  }
}

export default function TrendarArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const { data, isLoading, error } = useQuery({
    queryKey: ["v2", "trendar", slug],
    queryFn: () => fetchTrendBySlug(slug!),
    enabled: !!slug,
  });

  if (isLoading) {
    return (
      <BaseLayout title="Laddar… – Korthet" canonicalPath={`/trendar/${slug}`}>
        <p className="k2-loading">Laddar…</p>
      </BaseLayout>
    );
  }

  if (error || !data) return <NotFound />;

  const title = decodeEntities(data.title);
  const aiSummaryText = data.aiSummary
    ? decodeEntities(stripHtml(data.aiSummary))
    : "";
  const hasAi = aiSummaryText.length > 0 && (data.aiBullets?.length ?? 0) > 0;
  const description = hasAi
    ? aiSummaryText.slice(0, 155)
    : data.excerpt && data.excerpt !== data.title
      ? decodeEntities(data.excerpt)
      : `Trendande ämne på Google i Sverige: ${title}. Läs mer hos svenska nyhetskällor.`;

  const canonicalUrl = `${SITE}/trendar/${data.slug}`;
  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "NewsArticle",
      headline: title,
      description: description.slice(0, 300),
      datePublished: data.publishedAt ?? undefined,
      dateModified: data.publishedAt ?? undefined,
      inLanguage: "sv-SE",
      articleSection: "Trendar",
      mainEntityOfPage: { "@type": "WebPage", "@id": canonicalUrl },
      url: canonicalUrl,
      image: [`${SITE}/og-image.png`],
      author: {
        "@type": "Organization",
        name: "Korthet-redaktionen",
        url: `${SITE}/om-korthet/redaktionen`,
      },
      publisher: {
        "@type": "Organization",
        name: "Korthet",
        url: `${SITE}/`,
        logo: { "@type": "ImageObject", url: `${SITE}/og-image.png` },
      },
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Start", item: `${SITE}/` },
        { "@type": "ListItem", position: 2, name: "Trendar", item: `${SITE}/trendar` },
        { "@type": "ListItem", position: 3, name: title, item: canonicalUrl },
      ],
    },
  ];

  return (
    <BaseLayout
      title={`${title} – Trendar | Korthet`}
      description={description.slice(0, 155)}
      canonicalPath={`/trendar/${data.slug}`}
      ogType="article"
      jsonLd={jsonLd}
    >
      <article className="k2-article">
        <nav aria-label="Brödsmulor" className="k2-crumbs">
          <Link to="/trendar">← Alla trendar</Link>
        </nav>
        <div className="k2-article-meta-row">
          <span className="k2-cat-badge" data-cat="trendar">Trendar</span>
          {data.faktakaraktar && (
            <span
              className="k2-fakt-badge"
              data-fakt={data.faktakaraktar}
              title="Artikeltyp"
            >
              {FAKTAKARAKTAR_LABEL[data.faktakaraktar as keyof typeof FAKTAKARAKTAR_LABEL] ?? data.faktakaraktar}
            </span>
          )}
        </div>
        <h1 className="k2-h1">{title}</h1>
        {data.publishedAt && (
          <p className="k2-meta k2-article-meta">
            <time dateTime={data.publishedAt}>{formatDate(data.publishedAt)}</time>
          </p>
        )}
        <Transparency
          source={data.sources?.[0]?.name ?? "Google Trends"}
          sourceUrl={data.sources?.[0]?.url ?? null}
          publishedAt={data.publishedAt}
        />

        {hasAi ? (
          <>
            <section aria-labelledby="summary-heading">
              <h2 id="summary-heading" className="k2-h2">Nyheten i korthet</h2>
              <p className="k2-summary">{aiSummaryText}</p>
            </section>

            {data.aiBullets && data.aiBullets.length > 0 && (
              <section aria-labelledby="bullets-heading">
                <h2 id="bullets-heading" className="k2-h2">Viktiga punkter</h2>
                <ul className="k2-bullets">
                  {data.aiBullets.map((b, i) => (
                    <li key={i}>{decodeEntities(b)}</li>
                  ))}
                </ul>
              </section>
            )}

            {(data.huvudamne || (data.berordaAmnen && data.berordaAmnen.length > 0)) && (
              <section aria-labelledby="profile-heading" className="k2-profile">
                <h2 id="profile-heading" className="k2-h2">Artikelprofil</h2>
                {data.huvudamne && (
                  <p className="k2-profile-row">
                    <span className="k2-profile-label">Huvudämne:</span>{" "}
                    <span className="k2-chip k2-chip-primary">{amneLabel(data.huvudamne)}</span>
                  </p>
                )}
                {data.berordaAmnen && data.berordaAmnen.length > 0 && (
                  <p className="k2-profile-row">
                    <span className="k2-profile-label">Berörda ämnen:</span>{" "}
                    {data.berordaAmnen.map((s) => (
                      <span key={s} className="k2-chip">{amneLabel(s)}</span>
                    ))}
                  </p>
                )}
              </section>
            )}

            <p className="k2-disclaimer">
              Sammanfattning genererad av AI baserat på trendande svenska nyhetskällor. Originalinnehållet tillhör respektive publikation.
            </p>
          </>
        ) : data.excerpt && data.excerpt !== data.title ? (
          <p className="k2-lede">{decodeEntities(data.excerpt)}</p>
        ) : null}

        <h2 className="k2-h2">Källor</h2>
        {data.sources.length === 0 ? (
          <p className="k2-empty">Inga källor tillgängliga.</p>
        ) : (
          <ul className="k2-feed">
            {data.sources.map((s, i) => {
              const host = hostOf(s.url);
              const favicon = `https://www.google.com/s2/favicons?domain=${host}&sz=32`;
              return (
                <li key={`${s.url}-${i}`} className="k2-card">
                  <h3 className="k2-card-title">
                    <a href={s.url} rel="nofollow noopener external" target="_blank">
                      {host}
                    </a>
                  </h3>
                  <div className="k2-meta">
                    <span className="k2-source">
                      <img
                        src={favicon}
                        alt=""
                        width={16}
                        height={16}
                        className="k2-favicon"
                        loading="lazy"
                      />
                      {host}
                    </span>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </article>
    </BaseLayout>
  );
}
